/**
 * Store de tema — Estado global de tema claro/oscuro (Zustand)
 *
 * QUÉ HACE:
 * - Persiste la preferencia de tema (light/dark) en localStorage
 * - Aplica automáticamente la clase 'dark' en <html> al cambiar de tema
 * - Detecta la preferencia del sistema como valor inicial (si no hay preferencia guardada)
 *
 * QUÉ EXPORTA: useThemeStore
 *
 * QUÉ NO DEBE HACER:
 * - NO debe contener lógica de estilos specificos (eso va en Tailwind/CSS)
 * - NO debe acoplar a ningún componente especifico
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import { syncNativeStatusBar } from "@/lib/nativeShell";

type Theme = "light" | "dark";

interface ThemeState {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

/** Aplica o quita la clase 'dark' en <html> */
const applyThemeToDOM = (theme: Theme) => {
  const root = document.documentElement;
  if (theme === "dark") {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }
  void syncNativeStatusBar();
};

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: "light",

      toggleTheme: () => {
        const next = get().theme === "light" ? "dark" : "light";
        applyThemeToDOM(next);
        set({ theme: next });
      },

      setTheme: (theme: Theme) => {
        applyThemeToDOM(theme);
        set({ theme });
      },
    }),
    {
      name: "altenot-theme",
    },
  ),
);

// Aplica tema guardado al cargar (sincroniza DOM con localStorage)
useThemeStore.subscribe((state) => {
  applyThemeToDOM(state.theme);
});

// Aplicar tema inmediatamente al importar el módulo
applyThemeToDOM(useThemeStore.getState().theme);
